import * as z from "zod";
import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  CartItemNotFound,
  CheckoutRefused,
  CustomerCartNotFound,
  ErrorCode,
  InvalidFormField,
  PaymentsNotReady,
  ScansNotPurchasable,
  StoreNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import {
  AdminCustomerCart,
  Checkout,
  type CheckoutOptions,
  CustomerCart,
  CustomerCartCreated,
  type CustomerCartItemEdit,
  type CustomerCartStatus,
} from "../model/customer-cart";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const CartsResponse = z.object({ requests: z.array(CustomerCart) });
const AdminCartsResponse = z.object({ requests: z.array(AdminCustomerCart) });
const PendingCountResponse = z.object({ count: z.number() });

/** `id` names the cart on every per-cart route; `status` is a cart that is no longer OPEN. */
function cartFailure(customerCartId: string, e: ValidationError): CustomerCartNotFound | CheckoutRefused | null {
  if (e.incorrectValue === "id") return new CustomerCartNotFound(customerCartId, e);
  if (e.incorrectValue === "status") return new CheckoutRefused(e.detailedDesc, e);
  return null;
}

/**
 * `/customer-cart`: the cards a customer wants to buy from a shop, and the
 * payment for them. The customer builds it from scans the shop has approved;
 * the shop sees every cart at its counter.
 */
export class CustomerCartApi {
  constructor(private readonly t: Transport) {}

  /** Opens a cart at a shop from the caller's approved scans there. */
  async create(
    ctx: ApiContext,
    storeId: string,
    scanIds: string[],
  ): Promise<Result<CustomerCartCreated, ApiNotAuthorized | StoreNotFound | ScansNotPurchasable | InvalidFormField>> {
    const r = await this.t.post(ctx, `/customer-cart/${storeId}/create`, { body: { scanIds } });
    return forward(
      r,
      (j) => parse(CustomerCartCreated, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "scanIds" && e.code !== ErrorCode.ValueRequired) {
            return new ScansNotPurchasable(e.detailedDesc, e);
          }
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Staff open a cart on a customer's behalf, from scans approved for them. */
  async adminCreate(
    ctx: ApiContext,
    storeId: string,
    customerId: string,
    scanIds: string[],
  ): Promise<Result<CustomerCartCreated, ApiNotAuthorized | StoreNotFound | ScansNotPurchasable | InvalidFormField>> {
    const r = await this.t.post(ctx, `/customer-cart/${storeId}/admin/create`, { body: { customerId, scanIds } });
    return forward(
      r,
      (j) => parse(CustomerCartCreated, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "scanIds" && e.code !== ErrorCode.ValueRequired) {
            return new ScansNotPurchasable(e.detailedDesc, e);
          }
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** The caller's own carts at a shop, EXPIRED included so a lapsed cart can be explained. */
  async list(ctx: ApiContext, storeId: string): Promise<Result<CustomerCart[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/customer-cart/${storeId}/list`, { query: { includeExpired: "true" } });
    return forward(
      r,
      (j) => parse(CartsResponse, j).requests,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Every cart at a shop the caller administers, newest first, optionally one status. */
  async adminList(
    ctx: ApiContext,
    storeId: string,
    status?: CustomerCartStatus,
  ): Promise<Result<AdminCustomerCart[], ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/customer-cart/${storeId}/admin/list`, { query: { status } });
    return forward(
      r,
      (j) => parse(AdminCartsResponse, j).requests,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** How many carts are open at a shop the caller administers, or at `loc_<id>`: the badge on the counter. */
  async pendingCount(ctx: ApiContext, storeId: string): Promise<Result<number, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/customer-cart/${storeId}/pending-count`);
    return forward(
      r,
      (j) => parse(PendingCountResponse, j).count,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Adds approved scans to an open cart; what is already there is left alone. Returns the whole cart. */
  async addItems(
    ctx: ApiContext,
    customerCartId: string,
    scanIds: string[],
  ): Promise<
    Result<
      CustomerCart,
      ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | ScansNotPurchasable | InvalidFormField
    >
  > {
    const r = await this.t.patch(ctx, `/customer-cart/${customerCartId}/items`, { body: { scanIds } });
    return forward(
      r,
      (j) => parse(CustomerCart, j),
      (e) => {
        if (e instanceof ValidationError) {
          const known = cartFailure(customerCartId, e);
          if (known) return known;
          if (e.incorrectValue === "scanIds" && e.code !== ErrorCode.ValueRequired) {
            return new ScansNotPurchasable(e.detailedDesc, e);
          }
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Changes one item's quantity or whether it is included. Returns the whole cart. */
  async editItem(
    ctx: ApiContext,
    customerCartId: string,
    itemId: string,
    edit: CustomerCartItemEdit,
  ): Promise<
    Result<
      CustomerCart,
      ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | CartItemNotFound | InvalidFormField
    >
  > {
    const r = await this.t.patch(ctx, `/customer-cart/${customerCartId}/items/${itemId}`, { body: edit });
    return forward(
      r,
      (j) => parse(CustomerCart, j),
      (e) => {
        if (e instanceof ValidationError) {
          const known = cartFailure(customerCartId, e);
          if (known) return known;
          if (e.incorrectValue === "itemId") return new CartItemNotFound(itemId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Takes one item out. The last item cannot go: delete the cart instead. Returns the whole cart. */
  async removeItem(
    ctx: ApiContext,
    customerCartId: string,
    itemId: string,
  ): Promise<Result<CustomerCart, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | CartItemNotFound>> {
    const r = await this.t.delete(ctx, `/customer-cart/${customerCartId}/items/${itemId}`);
    return forward(
      r,
      (j) => parse(CustomerCart, j),
      (e) => {
        if (e instanceof ValidationError) {
          const known = cartFailure(customerCartId, e);
          if (known) return known;
          if (e.incorrectValue === "itemId") return new CartItemNotFound(itemId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Opens a payment for the cart and returns where to pay. Idempotent while a
   * checkout is live: calling twice returns the same one.
   */
  async checkout(
    ctx: ApiContext,
    customerCartId: string,
    options: CheckoutOptions = {},
  ): Promise<Result<Checkout, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused | PaymentsNotReady>> {
    const r = await this.t.post(ctx, `/customer-cart/${customerCartId}/checkout`, { body: options });
    return forward(
      r,
      (j) => parse(Checkout, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "id") return new CustomerCartNotFound(customerCartId, e);
          if (e.code === ErrorCode.IncorrectState) return new CheckoutRefused(e.detailedDesc, e);
          if (e.incorrectValue === "") return new PaymentsNotReady(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Deletes an open cart. The customer or the shop may; a paid one is refused. */
  async remove(
    ctx: ApiContext,
    customerCartId: string,
  ): Promise<Result<void, ApiNotAuthorized | CustomerCartNotFound | CheckoutRefused>> {
    const r = await this.t.delete(ctx, `/customer-cart/${customerCartId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "id") return new CustomerCartNotFound(customerCartId, e);
          if (e.incorrectValue === "status") return new CheckoutRefused(e.detailedDesc, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
